import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import FilterDrawer from '../components/FilterDrawer';
import { Edit, Trash2, Plus, MapPin, Users, Clock, Grid3x3, List, Search, SlidersHorizontal } from 'lucide-react';
import { getFirstImage } from '../utils/imageHelper';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { getListingTabs, filterListingsByTab, getSortingOptions, sortListings } from '../utils/listingTabsConfig';
import { getListingFilters, applyListingFilters } from '../utils/listingFiltersConfig';

const Volunteering = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ selectedFilters: {}, rangeValues: {} });

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const data = await apiCall('/api/listings/?listing_type=volunteering');
      setOpportunities(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load opportunities',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) return;
    
    try {
      await apiCall(`/api/listings/${id}`, { method: 'DELETE' });
      setOpportunities(opportunities.filter((o) => o.id !== id));
      toast({
        title: 'Success',
        description: 'Opportunity deleted successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete opportunity',
        variant: 'destructive'
      });
    }
  };

  // Get filtered and sorted opportunities
  const getDisplayOpportunities = () => {
    let filtered = opportunities;

    // Apply tab filter
    filtered = activeTab === 'all' ? filtered : filterListingsByTab(filtered, activeTab);

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(o => 
        o.title?.toLowerCase().includes(query) ||
        o.description?.toLowerCase().includes(query) ||
        o.category?.toLowerCase().includes(query)
      );
    }

    // Apply filters from drawer - note: Volunteering doesn't have price filter
    filtered = applyListingFilters(filtered, appliedFilters, 'Volunteering');

    // Apply sorting
    return sortListings(filtered, sortBy);
  };

  const displayOpportunities = getDisplayOpportunities();
  const tabs = getListingTabs(opportunities);
  const activeFiltersCount = Object.values(appliedFilters.selectedFilters).flat().length + 
    Object.keys(appliedFilters.rangeValues).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Seva / Volunteering Opportunities</h1>
            <p className="text-gray-600 mt-2">Manage volunteer and service opportunities</p>
          </div>
          <div className="flex gap-2">
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={() => navigate('/listings/volunteering/new')} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Opportunity
            </Button>
          </div>
        </div>

        {/* Tabs, Search, Filters, Sorting */}
        <Card>
          <CardContent className="p-4 md:p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-4">
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                <TabsList className="inline-flex md:grid md:grid-cols-6 md:w-full md:max-w-4xl gap-1 min-w-max md:min-w-0">
                  {tabs.map(tab => (
                    <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                      <tab.icon className="h-3 w-3 md:h-4 md:w-4" />
                      {tab.label} ({tab.count})
                    </TabsTrigger>
                  ))}
                  <TabsTrigger value="all" className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                    All ({opportunities.length})
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>

            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search opportunities by title, description, category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Button 
                variant="outline" 
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {getSortingOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Filter Drawer */}
        <FilterDrawer
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={getListingFilters(opportunities, 'Volunteering')}
          onApplyFilters={setAppliedFilters}
          activeFiltersCount={activeFiltersCount}
        />

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-gray-500">Loading opportunities...</div>
          </div>
        ) : displayOpportunities.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No opportunities found</p>
            <Button onClick={() => navigate('/listings/volunteering/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create your first opportunity
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayOpportunities.map((opportunity) => (
              <Card key={opportunity.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/listings/volunteering/${opportunity.id}`)}>
                {getFirstImage(opportunity.media) ? (
                  <img
                    src={getFirstImage(opportunity.media)}
                    alt={opportunity.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">No image</span>
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900 text-lg">{opportunity.title}</h3>
                    <Badge variant={opportunity.status === 'active' ? 'default' : 'secondary'}>
                      {opportunity.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {opportunity.description}
                  </p>
                  <div className="space-y-2 mb-3">
                    {opportunity.data?.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4" />
                        {opportunity.data.location}
                      </div>
                    )}
                    {opportunity.data?.timeCommitment && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4" />
                        {opportunity.data.timeCommitment}
                      </div>
                    )}
                    {opportunity.data?.numberOfVolunteers && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="h-4 w-4" />
                        {opportunity.data.numberOfVolunteers} volunteers needed
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <Badge variant="outline">{opportunity.category}</Badge>
                      {opportunity.data?.isOngoing && (
                        <Badge className="bg-green-600 text-white">
                          Ongoing
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/listings/volunteering/edit/${opportunity.id}`); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDelete(opportunity.id); }}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Opportunity</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Category</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Location</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Commitment</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayOpportunities.map((opportunity) => (
                    <tr key={opportunity.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/listings/volunteering/${opportunity.id}`)}>
                      <td className="p-4 font-medium">{opportunity.title}</td>
                      <td className="p-4">
                        <Badge variant="outline">{opportunity.category}</Badge>
                      </td>
                      <td className="p-4">{opportunity.data?.location || '-'}</td>
                      <td className="p-4">{opportunity.data?.timeCommitment || '-'}</td>
                      <td className="p-4">
                        <Badge variant={opportunity.status === 'active' ? 'default' : 'secondary'}>
                          {opportunity.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/listings/volunteering/edit/${opportunity.id}`); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(opportunity.id); }}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Volunteering;
